<div class="p-4 space-y-3">

  <!-- Header row inside the pane — Reload + Re-pull from GitHub live
       here so they're scoped to THIS source, not the whole panel. -->
  <div class="flex items-center gap-3 flex-wrap">
    <h2 class="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
      <i class="fa-brands fa-github text-app-teal-100"></i>GitHub Symbol Lists
    </h2>
    <button class="dark-btn dark-btn-sm" (click)="reload()" [disabled]="loading">
      <i class="fa-solid fa-rotate-right mr-1" [class.fa-spin]="loading"></i>Reload
    </button>
    <button class="dark-btn dark-btn-sm" (click)="manualRefresh()" [disabled]="loading" title="Force a pull from the source repo — normally the webhook does this automatically on push.">
      <i class="fa-brands fa-github mr-1"></i>Re-pull from GitHub
    </button>
    <!-- Dynamic source token: prefer the selected list's Redis key
         (symbol-list:<name>), fall back to the repo path when no list
         is picked yet. -->
    <app-copy-source-button
      [source]="selectedName ? ('symbol-list:' + selectedName) : 'F2Tech-WebDev-v2/common-symbol-lists'"
      [tooltip]="selectedName ? ('Redis key for the ' + selectedName + ' list') : 'GitHub repo — pick a list to get the per-list Redis key'">
    </app-copy-source-button>
    <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">
      <strong>{{ lists.length }}</strong> list<span *ngIf="lists.length !== 1">s</span>
      currently in Redis
    </span>
  </div>

  <p class="text-xs text-gray-500 dark:text-gray-400">
    Source of truth is
    <code class="text-app-teal-100">F2Tech-WebDev-v2/common-symbol-lists</code>
    on GitHub. Every save here becomes a commit on <code>main</code>; the service kicks a
    Redis refresh immediately, so consumers see the new list as soon as this call returns.
    Two file formats supported: <code>.txt</code> (one symbol per line, uppercase,
    <code>#</code> comments and blank lines ignored) or <code>.csv</code> (first row is
    headers, one column names the ticker — pick it in the selector when creating).
  </p>

  <div class="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4">

    <!-- Left: list picker -->
    <div class="border border-gray-200 dark:border-[#434863] rounded bg-white dark:bg-app-black-100 overflow-hidden">
      <div class="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-[#434863]">
        <span class="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Lists</span>
        <button class="dark-btn dark-btn-sm ml-auto" (click)="startNew()" title="Create a new list">
          <i class="fa-solid fa-plus mr-1"></i>New list
        </button>
      </div>
      <ul *ngIf="lists.length > 0" class="divide-y divide-gray-200 dark:divide-[#434863]">
        <li *ngFor="let l of lists; trackBy: trackByName">
          <div class="flex items-stretch" [class.bg-app-blue-100]="selectedName === l.name">
            <button
              class="flex-1 text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-app-blue-100/30 transition"
              (click)="selectList(l.name)">
              <div class="text-sm font-mono text-gray-900 dark:text-white">{{ l.name }}</div>
              <div class="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                {{ l.count }} symbol<span *ngIf="l.count !== 1">s</span>
                <span *ngIf="l.manifest?.commit_sha as sha">
                  · <code class="text-[10px]">{{ sha.slice(0, 7) }}</code>
                </span>
                <span *ngIf="l.manifest?.refreshed_at as when" class="ml-1">
                  · {{ when | date:'MMM d, y, h:mm a' }}
                </span>
              </div>
            </button>
            <button
              class="px-3 hover:bg-gray-100 dark:hover:bg-app-black-200 border-l border-gray-200 dark:border-[#434863] text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition"
              (click)="openDefinition(l.name, $event)"
              title="Edit registry definition (repo / path / format / ticker column)">
              <i class="fa-solid fa-gear text-xs"></i>
            </button>
          </div>
        </li>
      </ul>
      <div *ngIf="lists.length === 0 && !loading" class="px-3 py-6 text-center text-xs text-gray-500 dark:text-gray-400">
        No lists in Redis yet. Click <strong>New list</strong> or push a file to the repo.
      </div>
    </div>

    <!-- Right: editor -->
    <div class="border border-gray-200 dark:border-[#434863] rounded bg-white dark:bg-app-black-100 overflow-hidden">

      <div *ngIf="mode === 'idle'" class="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
        Pick a list on the left to view or edit, or click <strong>New list</strong> to add one.
      </div>

      <div *ngIf="mode !== 'idle'" class="flex flex-col">
        <div class="flex items-center gap-3 px-3 py-2 border-b border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100 flex-wrap">
          <ng-container *ngIf="mode === 'view'">
            <span class="font-mono text-sm text-gray-900 dark:text-white">{{ selectedName }}.{{ viewFormat }}</span>
            <span class="text-[10px] text-gray-500 dark:text-gray-400" *ngIf="editorSha">
              sha <code>{{ editorSha.slice(0, 7) }}</code>
            </span>
          </ng-container>
          <ng-container *ngIf="mode === 'new'">
            <!-- Repo picker (IT-F2-100) — dropdown of repos currently
                 in use by registered lists, plus an Other… sentinel
                 that reveals a text field for a brand-new repo slug.
                 Defaults to F2Tech-WebDev-v2/common-symbol-lists so
                 single-repo shops don't have to touch it. -->
            <span class="text-xs text-gray-500 dark:text-gray-400">Repo:</span>
            <select
              class="border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs font-mono w-72"
              [ngModel]="newRepo"
              (ngModelChange)="onRepoSelectChange($event)">
              <option *ngFor="let r of knownRepos" [value]="r">{{ r }}</option>
              <option [value]="REPO_OTHER">Other (type new repo)…</option>
            </select>
            <input
              *ngIf="repoInputMode === 'other'"
              type="text"
              class="border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs font-mono w-72"
              [ngModel]="newRepoOther"
              (ngModelChange)="onRepoOtherChange($event)"
              placeholder="owner/name — e.g. F2Tech-WebDev-v2/theo-trade-symbol-lists"
              autofocus />


            <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">Name:</span>
            <span class="text-gray-500 dark:text-gray-400 text-xs">lists/</span>
            <input
              type="text"
              class="border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs font-mono w-48"
              [(ngModel)]="newName"
              (ngModelChange)="onNewNameChange()"
              placeholder="e.g. sp500"
              title="lowercase letters, digits, hyphens, underscores. Must start with a letter or digit. Max 64 chars. Anything else is auto-hyphenated as you type."
              autofocus />
            <span class="text-gray-500 dark:text-gray-400 text-xs">.{{ newFormat }}</span>

            <!-- Type toggle — new-list only. Radio semantics via role
                 pair so keyboard nav (Tab into the group, arrows to
                 switch) works. Extension stays visible in the label
                 above so the operator sees the effect of the pick. -->
            <div class="ml-3 inline-flex text-xs items-center gap-1 border border-gray-300 dark:border-[#434863] rounded overflow-hidden">
              <button type="button"
                class="px-2 py-1 font-mono transition"
                [class.bg-app-blue-100]="newFormat === 'txt'"
                [class.text-white]="newFormat === 'txt'"
                [class.text-gray-500]="newFormat !== 'txt'"
                (click)="onFormatChange('txt')"
                title="One symbol per line, uppercase">.txt</button>
              <button type="button"
                class="px-2 py-1 font-mono transition border-l border-gray-300 dark:border-[#434863]"
                [class.bg-app-blue-100]="newFormat === 'csv'"
                [class.text-white]="newFormat === 'csv'"
                [class.text-gray-500]="newFormat !== 'csv'"
                (click)="onFormatChange('csv')"
                title="Header row + comma-separated columns; pick which column holds the ticker">.csv</button>
            </div>

            <!-- Upload-file button — auto-fills name+type+content from
                 the picked file. Hidden native <input> triggered by the
                 dark-btn so the styling matches the rest of the toolbar. -->
            <button type="button"
              class="dark-btn dark-btn-sm ml-2"
              (click)="triggerFileInput()"
              title="Read a .txt or .csv from disk — name and type auto-fill from the filename">
              <i class="fa-solid fa-file-arrow-up mr-1"></i>Upload file…
            </button>
            <input #fileInput type="file" accept=".txt,.csv"
              class="hidden"
              (change)="onFileSelected($event)" />
          </ng-container>
          <span class="ml-auto text-[11px] text-gray-500 dark:text-gray-400">
            <strong>{{ parsedCount }}</strong> valid symbol<span *ngIf="parsedCount !== 1">s</span>
            <span *ngIf="editorDirty" class="ml-2 text-amber-600 dark:text-amber-300">• unsaved</span>
          </span>
        </div>

        <!-- CSV ticker-column selector — new-list + csv + at-least-one-
             header. Auto-picks Symbol/Ticker/Sym on content change; the
             operator can override and their pick sticks. -->
        <div *ngIf="mode === 'new' && newFormat === 'csv' && csvHeaders.length > 0"
             class="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100">
          <span class="text-xs text-gray-500 dark:text-gray-400">Ticker column:</span>
          <select
            class="border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-2 py-1 text-xs font-mono"
            [ngModel]="newTickerColumn"
            (ngModelChange)="onTickerColumnChange($event)">
            <option *ngFor="let h of csvHeaders" [value]="h">{{ h }}</option>
          </select>
          <span class="text-[11px] text-gray-500 dark:text-gray-400">
            parsed from header row · {{ csvHeaders.length }} column<span *ngIf="csvHeaders.length !== 1">s</span>
          </span>
        </div>

        <textarea
          class="w-full min-h-[420px] px-3 py-2 font-mono text-xs bg-white dark:bg-app-black-100 text-gray-900 dark:text-white border-0 outline-none resize-y"
          [(ngModel)]="editorContent"
          (ngModelChange)="onContentChange()"
          spellcheck="false"
          [placeholder]="(mode === 'new' && newFormat === 'csv')
            ? 'Symbol,Name,Exchange&#10;AAPL,Apple,NASDAQ&#10;MSFT,Microsoft,NASDAQ&#10;NVDA,NVIDIA,NASDAQ'
            : '# One symbol per line, uppercase.&#10;# Comments (#) and blank lines are ignored.&#10;AAPL&#10;MSFT&#10;NVDA'"></textarea>

        <div class="flex items-center gap-2 px-3 py-2 border-t border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100">
          <button
            class="dark-btn dark-btn-primary dark-btn-sm"
            (click)="save()"
            [disabled]="saving || !editorContent.trim() || (mode === 'new' && !newName.trim())">
            <i class="fa-solid fa-floppy-disk mr-1"></i>
            <span *ngIf="!saving">{{ mode === 'new' ? 'Create + commit' : 'Save + commit' }}</span>
            <span *ngIf="saving">Committing…</span>
          </button>
          <button
            *ngIf="mode === 'view'"
            class="dark-btn dark-btn-danger dark-btn-sm"
            (click)="remove()"
            [disabled]="deleting">
            <i class="fa-solid fa-trash mr-1"></i>
            <span *ngIf="!deleting">Delete</span>
            <span *ngIf="deleting">Deleting…</span>
          </button>
          <button class="dark-btn dark-btn-sm ml-auto" (click)="cancelEdit()">
            Cancel
          </button>
        </div>
      </div>

    </div>
  </div>
</div>
